import { useQuery } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import type { TourWithSteps } from "@shared/schema";
import { getCategoryFromTargetPage, TOUR_CATEGORY_LABELS } from "@shared/schema";
import { PlayCircle, CheckCircle, Clock, Search, RotateCcw, Sparkles } from "lucide-react";
import { useState } from "react";
import { useTourPlayer } from "@/components/tour-player-context";

const difficultyColors = {
  beginner: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300",
  intermediate: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300",
  advanced: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300",
};

export default function ToursPage() {
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [searchQuery, setSearchQuery] = useState("");
  const { startTour } = useTourPlayer();

  const { data: tours = [], isLoading } = useQuery<TourWithSteps[]>({
    queryKey: ["/api/tours"],
  });

  const filteredTours = tours.filter((tour) => {
    const tourCategory = getCategoryFromTargetPage(tour.targetPage);
    const matchesCategory = selectedCategory === "all" || tourCategory === selectedCategory;
    const matchesSearch = tour.title.toLowerCase().includes(searchQuery.toLowerCase()) || 
                          (tour.description && tour.description.toLowerCase().includes(searchQuery.toLowerCase()));
    return matchesCategory && matchesSearch;
  });

  const categories = ["all", ...Array.from(new Set(tours.map((t) => getCategoryFromTargetPage(t.targetPage))))];

  const getProgressPercentage = (tour: TourWithSteps): number => {
    const steps = tour.steps || [];
    if (!tour.progress || !steps.length) return 0;
    const completedSteps = Array.isArray(tour.progress.completedSteps) ? tour.progress.completedSteps : [];
    return Math.round((completedSteps.length / steps.length) * 100);
  };

  const getTourStatus = (tour: TourWithSteps): "not_started" | "in_progress" | "completed" => {
    if (!tour.progress) return "not_started";
    return tour.progress.status as "not_started" | "in_progress" | "completed";
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="w-full max-w-full p-4 md:p-6">
      <div className="mb-8">
        <div className="flex items-center gap-2 mb-2">
          <Sparkles className="h-8 w-8 text-primary" />
          <h1 className="text-3xl font-bold">Guided Tours</h1>
        </div>
        <p className="text-muted-foreground">
          Interactive step-by-step tutorials to help you master GoalVia features
        </p>
      </div>

      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            data-testid="input-search-tours"
            placeholder="Search tours..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={selectedCategory} onValueChange={setSelectedCategory}>
          <SelectTrigger data-testid="select-category-filter" className="w-full sm:w-[200px]">
            <SelectValue placeholder="All categories" />
          </SelectTrigger>
          <SelectContent>
            {categories.map((cat) => (
              <SelectItem key={cat} value={cat}>
                {cat === "all" ? "All Categories" : TOUR_CATEGORY_LABELS[cat] || cat}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {filteredTours.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16">
            <Sparkles className="h-16 w-16 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">No tours found</h3>
            <p className="text-muted-foreground text-center">
              {searchQuery || selectedCategory !== "all"
                ? "Try adjusting your filters"
                : "Tours will appear here once they are created"}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {filteredTours.map((tour) => {
            const status = getTourStatus(tour);
            const progressPercentage = getProgressPercentage(tour);

            return (
              <div 
                key={tour.id} 
                data-testid={`row-tour-${tour.id}`} 
                className="flex items-center gap-4 p-4 bg-card border rounded-lg hover:bg-accent/50 transition-colors"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    {status === "completed" && (
                      <CheckCircle className="h-4 w-4 text-green-500 shrink-0" />
                    )}
                    <span className="font-medium">{tour.title}</span>
                    <Badge 
                      className={`shrink-0 ${difficultyColors[tour.difficulty as keyof typeof difficultyColors] || difficultyColors.beginner}`}
                      data-testid={`badge-difficulty-${tour.id}`}
                    >
                      {tour.difficulty}
                    </Badge>
                    <Badge variant="outline" className="shrink-0" data-testid={`badge-category-${tour.id}`}>
                      {TOUR_CATEGORY_LABELS[getCategoryFromTargetPage(tour.targetPage)] || getCategoryFromTargetPage(tour.targetPage)}
                    </Badge>
                  </div>
                  {tour.description && (
                    <p className="text-sm text-muted-foreground truncate">{tour.description}</p>
                  )}
                  {status !== "not_started" && (
                    <div className="flex items-center gap-2 mt-2">
                      <Progress value={progressPercentage} className="h-2 w-32" />
                      <span className="text-xs text-muted-foreground">{progressPercentage}%</span>
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-3 text-sm text-muted-foreground shrink-0">
                  <div className="flex items-center gap-1">
                    <Clock className="h-4 w-4" />
                    <span>{tour.estimatedMinutes} min</span>
                  </div>
                  <span>{(tour.steps || []).length} steps</span>
                </div>
                <div className="shrink-0">
                  {status === "not_started" && (
                    <Button
                      data-testid={`button-start-tour-${tour.id}`}
                      size="sm"
                      onClick={() => startTour(tour.id)}
                    >
                      <PlayCircle className="h-4 w-4 mr-1" />
                      Start
                    </Button>
                  )}
                  {status === "in_progress" && (
                    <Button
                      data-testid={`button-continue-tour-${tour.id}`}
                      size="sm"
                      onClick={() => startTour(tour.id)}
                    >
                      <PlayCircle className="h-4 w-4 mr-1" />
                      Continue
                    </Button>
                  )}
                  {status === "completed" && (
                    <div className="flex gap-1">
                      <Button
                        data-testid={`button-review-tour-${tour.id}`}
                        variant="outline"
                        size="sm"
                        onClick={() => startTour(tour.id)}
                      >
                        Review
                      </Button>
                      <Button
                        data-testid={`button-restart-tour-${tour.id}`}
                        variant="ghost"
                        size="sm"
                        onClick={() => startTour(tour.id, true)}
                      >
                        <RotateCcw className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
